import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImagePlus, X, Check } from "lucide-react";

export const INVESTMENT_RANGES = [
  "Under $10K",
  "$10K–$25K",
  "$25K–$50K",
  "$50K–$100K",
  "$100K+",
] as const;

export const PROJECT_TYPES = [
  "Desert Landscape Design",
  "Installation",
  "Exterior Edit",
  "Plant Autopilot — Private Stewardship",
  "Not sure yet",
] as const;

export const TIMING_OPTIONS = [
  "As soon as possible",
  "Within 1–3 months",
  "Within 3–6 months",
  "6+ months out",
  "Flexible",
] as const;

interface FormState {
  name: string;
  email: string;
  phone: string;
  address: string;
  projectType: string;
  investment: string;
  timing: string;
  notWorking: string;
  desiredFeel: string;
}

const initialState: FormState = {
  name: "",
  email: "",
  phone: "",
  address: "",
  projectType: "",
  investment: "",
  timing: "",
  notWorking: "",
  desiredFeel: "",
};

const labelClass =
  "block text-xs font-medium uppercase tracking-[0.18em] text-desert-olive-deep mb-2";
const inputClass =
  "bg-transparent border-0 border-b border-desert-stone/60 rounded-none px-0 py-3 text-desert-ink placeholder:text-desert-stone focus-visible:ring-0 focus-visible:border-desert-olive-deep transition-colors";

const ConsultationForm: React.FC = () => {
  const [form, setForm] = useState<FormState>(initialState);
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key: keyof FormState) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handlePhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setPhotos((prev) => [...prev, ...files]);
    setPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    e.target.value = "";
  };

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // TODO(backend): persist as a Lead in the internal system once the
    // replacement backend lands (Phase 2/3). Photos accompany the lead.
    console.info("Consultation request captured (pending backend)", {
      ...form,
      photoCount: photos.length,
    });

    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
    }, 600);
  };

  if (submitted) {
    return (
      <div className="border border-desert-stone/50 p-10 md:p-14 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-desert-olive-deep mb-6">
          <Check className="w-5 h-5 text-desert-olive-deep" />
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-desert-ink mb-4">
          Thank you, {form.name.split(" ")[0]}.
        </h3>
        <p className="text-desert-charcoal/80 max-w-md mx-auto leading-relaxed">
          We review every inquiry personally and respond within two business
          days. If the property is a fit, the next step is a walk of the site.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
        <div>
          <Label htmlFor="c-name" className={labelClass}>
            Name *
          </Label>
          <Input
            id="c-name"
            required
            value={form.name}
            onChange={(e) => set("name")(e.target.value)}
            placeholder="Full name"
            className={inputClass}
          />
        </div>
        <div>
          <Label htmlFor="c-email" className={labelClass}>
            Email *
          </Label>
          <Input
            id="c-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => set("email")(e.target.value)}
            placeholder="you@example.com"
            className={inputClass}
          />
        </div>
        <div>
          <Label htmlFor="c-phone" className={labelClass}>
            Phone
          </Label>
          <Input
            id="c-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => set("phone")(e.target.value)}
            placeholder="(760) 555-0000"
            className={inputClass}
          />
        </div>
        <div>
          <Label htmlFor="c-address" className={labelClass}>
            Property Address *
          </Label>
          <Input
            id="c-address"
            required
            value={form.address}
            onChange={(e) => set("address")(e.target.value)}
            placeholder="Street, Palm Springs, CA"
            className={inputClass}
          />
        </div>
        <div>
          <Label className={labelClass}>Project Type *</Label>
          <Select required value={form.projectType} onValueChange={set("projectType")}>
            <SelectTrigger className={`${inputClass} h-auto`}>
              <SelectValue placeholder="Select a project type" />
            </SelectTrigger>
            <SelectContent>
              {PROJECT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelClass}>Approximate Investment *</Label>
          <Select required value={form.investment} onValueChange={set("investment")}>
            <SelectTrigger className={`${inputClass} h-auto`}>
              <SelectValue placeholder="Select a range" />
            </SelectTrigger>
            <SelectContent>
              {INVESTMENT_RANGES.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelClass}>Desired Timing</Label>
          <Select value={form.timing} onValueChange={set("timing")}>
            <SelectTrigger className={`${inputClass} h-auto`}>
              <SelectValue placeholder="Select timing" />
            </SelectTrigger>
            <SelectContent>
              {TIMING_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="c-notworking" className={labelClass}>
          What is not working about the property right now? *
        </Label>
        <Textarea
          id="c-notworking"
          required
          value={form.notWorking}
          onChange={(e) => set("notWorking")(e.target.value)}
          placeholder="Be candid — the more specific, the better the consultation."
          className={`${inputClass} min-h-[100px] resize-y`}
        />
      </div>

      <div>
        <Label htmlFor="c-feel" className={labelClass}>
          What do you want the exterior to feel like? *
        </Label>
        <Textarea
          id="c-feel"
          required
          value={form.desiredFeel}
          onChange={(e) => set("desiredFeel")(e.target.value)}
          placeholder="Calm, architectural, lush, spare, private… describe the feeling."
          className={`${inputClass} min-h-[100px] resize-y`}
        />
      </div>

      <div>
        <Label className={labelClass}>Property Photos</Label>
        <p className="text-sm text-desert-charcoal/60 mb-4">
          Optional — a few photos of the exterior help us prepare for the
          consultation.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handlePhotos}
        />
        <div className="flex flex-wrap gap-4">
          {previews.map((src, i) => (
            <div
              key={src}
              className="relative w-24 h-24 border border-desert-stone/50 overflow-hidden"
            >
              <img
                src={src}
                alt={photos[i]?.name ?? `Property photo ${i + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                aria-label={`Remove photo ${i + 1}`}
                className="absolute top-1 right-1 bg-desert-ink/70 text-desert-cream rounded-full p-1"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-24 h-24 border border-dashed border-desert-stone flex flex-col items-center justify-center gap-1 text-desert-olive-deep hover:border-desert-olive-deep transition-colors"
          >
            <ImagePlus className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-widest">Add</span>
          </button>
        </div>
      </div>

      <div className="pt-4">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full md:w-auto bg-desert-ink text-desert-cream hover:bg-desert-olive-deep rounded-none px-12 py-6 text-sm uppercase tracking-[0.2em] font-medium"
        >
          {isSubmitting ? "Sending…" : "Request a Property Consultation"}
        </Button>
      </div>
    </form>
  );
};

export default ConsultationForm;
